// Path: src/components/auth/register/OLSMERegister.tsx
// Added referral code parsing from URL and manual referral input field
// Tracks referrals in Firestore and applies 5% commission for referrers
import React, { useState, useEffect, ChangeEvent } from "react";
import { withFirebase, WithFirebaseProps } from "../../dev/Firebase/Firebase";
import { UserRole } from "../../../constants/UserEnums";
import {
  Button,
  Checkbox,
  Form,
  Input,
  Radio,
  Tooltip,
  Typography,
  message,
  RadioChangeEvent,
  Divider,
  Breadcrumb,
} from "antd";
import { useNavigate, Link as RouterLink, useLocation } from "react-router-dom";
import styled from "styled-components";
import BackButton from "../../dev/Styled/BackButton";
import { useUserStore } from "../../../stores/UserStore";
import Spinner from "../../dev/utils/Spinner";
import { QuestionCircleOutlined, UserOutlined, InfoCircleOutlined, HomeOutlined, MailOutlined, GlobalOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet-async";
import { doc, setDoc, collection, getDocs, increment } from "firebase/firestore";
import "./OLSMERegister.css";

const { Title, Text } = Typography;

// Define the form values type
interface OLSMEFormValues {
  email: string;
  password?: string;
  confirm?: string;
  name: string;
  reference: string;
  referralCode?: string; // Added referralCode field
  location: string;
  olsExperience: string;
  accountType: string;
  agreement1: boolean;
  agreement2: boolean;
  credits?: string[];
}

// Define the state type
interface OLSMEState {
  confirmDirty: boolean;
  radioValue0: string;
  checkboxOther: string;
  checkboxOtherChecked: boolean;
  isSubmitting: boolean;
}

const RegisterContainer = styled.div`
  animation: fadeIn 1.5s ease-in;
  background: linear-gradient(135deg, #FFC107 0%, #FFCA28 100%);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  width: 100%;
  box-sizing: border-box;
  position: relative;
`;

const RegisterCard = styled.div`
  width: 95%;
  max-width: 100%;
  margin: 0 auto;
  background: #fce248;
  box-shadow: 0 6px 20px rgba(255, 204, 0, 0.4);
  border: 1px solid #ffeb3b;
  border-radius: 12px;
  padding: 40px;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 768px) {
    width: 96% !important;
    padding: 25px;
  }

  @media (max-width: 480px) {
    padding: 15px;
  }
`;

const StyledForm = styled(Form)`
  padding: 24px;

  .ant-form-item-label > label {
    font-size: 18px;
    color: #0427f4;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .ant-form-item {
    margin-bottom: 20px;
  }

  .ant-input,
  .ant-input-number,
  .ant-picker,
  .ant-time-picker {
    border-radius: 8px;
    padding: 8px;
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
  }

  .ant-input:hover,
  .ant-input-number:hover,
  .ant-picker:hover,
  .ant-time-picker:hover {
    border-color: #40c4ff;
  }

  .ant-input:focus,
  .ant-input-number:focus,
  .ant-picker:focus,
  .ant-time-picker:focus {
    border-color: #40c4ff;
    box-shadow: 0 0 0 2px rgba(64, 196, 255, 0.2);
  }

  .ant-radio-wrapper,
  .ant-checkbox-wrapper {
    font-size: 16px;
    color: #0427f4;
    transition: transform 0.3s ease;
  }

  .ant-radio-wrapper:hover,
  .ant-checkbox-wrapper:hover {
    transform: translateX(5px);
  }

  .ant-radio-wrapper:focus-within,
  .ant-checkbox-wrapper:focus-within {
    outline: 2px solid #40c4ff;
    outline-offset: 2px;
  }

  .ant-btn {
    font-size: 18px;
    border-radius: 8px;
    padding: 12px 24px;
    width: 100%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  .ant-btn:hover,
  .ant-btn:focus {
    transform: scale(1.02);
  }

  .ant-btn:focus {
    outline: 2px solid #40c4ff;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    padding: 15px;

    .ant-form-item-label > label,
    .ant-input,
    .ant-input-number,
    .ant-picker,
    .ant-time-picker,
    .ant-radio-wrapper,
    .ant-checkbox-wrapper,
    .ant-btn {
      font-size: 14px !important;
    }
  }

  @media (max-width: 480px) {
    .ant-form-item-label > label {
      font-size: 16px !important;
    }

    .ant-input,
    .ant-input-number,
    .ant-picker,
    .ant-time-picker,
    .ant-radio-wrapper,
    .ant-checkbox-wrapper,
    .ant-btn {
      font-size: 12px !important;
    }
  }
`;

const CreditsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 15px;
  padding: 10px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const SectionDivider = styled(Divider)`
  .ant-divider-inner-text {
    font-size: 20px;
    color: #0427f4;
    font-weight: 500;
  }
`;

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #0427f4;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #40c4ff;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const radioStyle: React.CSSProperties = {
  display: "block",
  height: "40px",
  lineHeight: "40px",
  color: "#0427f4",
};

const OLSMERegister: React.FC<WithFirebaseProps> = ({ firebase }) => {
  const [form] = Form.useForm<OLSMEFormValues>();
  const [state, setState] = useState<OLSMEState>({
    confirmDirty: false,
    radioValue0: "New User",
    checkboxOther: "",
    checkboxOtherChecked: false,
    isSubmitting: false,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const userStore = useUserStore();

  // Parse referral code from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const refCode = searchParams.get("ref");
    if (refCode) {
      form.setFieldsValue({ referralCode: refCode });
      console.log("Referral code from URL:", refCode);
    }
  }, [location, form]);

  useEffect(() => {
    if (!userStore.isLoaded) return;
    const user = userStore.user;
    const initialValues: OLSMEFormValues = user
      ? {
          email: user.email || "",
          name: user.name || "",
          reference: user.reference || "",
          referralCode: user.referralCode || "", // Added referralCode
          location: user.loc || "",
          olsExperience: user.olsExperience || "New User",
          accountType: user.accountType || "Individual",
          agreement1: user.agreement1 || false,
          agreement2: user.agreement2 || false,
          credits: user.credits || [],
        }
      : {
          email: "",
          name: "",
          reference: "",
          referralCode: "", // Added referralCode
          location: "",
          olsExperience: "New User",
          accountType: "Individual",
          agreement1: false,
          agreement2: false,
          credits: [],
        };
    setState((prev) => ({
      ...prev,
      radioValue0: initialValues.olsExperience,
    }));
    form.setFieldsValue(initialValues);
    console.log("OLSMERegister: Form initialized with values:", initialValues);
  }, [userStore.isLoaded, userStore.user, form]);

  const handleSubmit = async (values: OLSMEFormValues) => {
    console.log("Submitting OLSME registration with values:", values);
    setState((prev) => ({ ...prev, isSubmitting: true }));
    const { email, password, referralCode } = values;

    try {
      if (!firebase) {
        throw new Error("Firebase is not initialized");
      }

      const userData = {
        name: values.name,
        reference: values.reference,
        referralCode: referralCode || "", // Added referralCode
        loc: values.location,
        olsExperience: values.olsExperience,
        accountType: values.accountType,
        userRole: UserRole.OLSME_COL,
        agreement1: values.agreement1,
        agreement2: values.agreement2,
        credits: values.credits || [],
      };

      if (!userStore.isLoggedIn && email && password) {
        console.log("Creating new user with:", email);
        const userCredential = await firebase.auth.createUserWithEmailAndPassword(email, password);
        const user = userCredential.user;
        console.log("User created:", user.uid);

        userData.email = user.email;
        userData.uid = user.uid;
        userData.createdAt = firebase.firestore.FieldValue.serverTimestamp();
        const userDocRef = firebase.firestore.doc(firebase.firestore.db, "users", user.uid);
        await firebase.firestore.setDoc(userDocRef, userData);
        console.log("User data saved to Firestore:", user.uid);

        // Track referral if referralCode exists
        if (referralCode) {
          const db = firebase.firestore.db;
          const referralCollection = collection(db, `referral_links`);
          const referralDocs = await getDocs(referralCollection);
          let referrerUid: string | null = null;

          // Find the referrer's UID by matching the referral code
          referralDocs.forEach((doc) => {
            const data = doc.data();
            if (data.link?.includes(referralCode)) {
              referrerUid = doc.ref.parent.parent?.id || null;
            }
          });

          if (referrerUid) {
            const conversionRef = doc(collection(db, `referral_conversions`));
            await setDoc(conversionRef, {
              referrerUid,
              referredUid: user.uid,
              referralCode,
              commission: 0.05, // 5% commission
              createdAt: new Date(),
            });
            console.log("Referral conversion recorded:", { referrerUid, referredUid: user.uid, referralCode });

            // Increment conversions count in referral_links
            const referralDoc = doc(db, `referral_links/${referrerUid}/codes`, referralCode);
            await updateDoc(referralDoc, {
              conversions: increment(1),
            });
          } else {
            console.warn("No matching referrer found for referral code:", referralCode);
          }
        }

        try {
          await user.sendEmailVerification({ url: "https://www.olsme.com/login" });
          console.log("Verification email sent to:", user.email);
          message.success("Registration successful! Please check your email (including spam) to verify your account.");
        } catch (emailError: any) {
          console.error("Email verification error:", emailError.code, emailError.message);
          message.warning("Registration successful, but verification email failed. Check spam or verify manually in your profile.");
        }

        const userDoc = await firebase.firestore.getDoc(userDocRef);
        const fullUserData = userDoc.data();
        userStore.setUser({
          ...fullUserData,
          emailVerified: user.emailVerified,
        });
        console.log("UserStore synced with full data:", fullUserData);
        navigate("/login");
      } else if (userStore.isLoggedIn) {
        console.log("Updating user:", userStore.user.uid);
        const userDocRef = firebase.firestore.doc(firebase.firestore.db, "users", userStore.user.uid);
        await firebase.firestore.updateDoc(userDocRef, userData);
        userStore.setUser({ ...userStore.user, ...userData });
        message.success("Account updated successfully!");
        navigate("/home/profile");
      } else {
        throw new Error("Invalid submission state: missing email/password for new user");
      }
    } catch (error: any) {
      console.error("OLSME Registration error:", error.code || "undefined", error.message || error);
      message.error(`Registration failed: ${error.message || error.code || "Please try again."}`);
    } finally {
      setState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const handleCheckboxOther = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setState({ ...state, checkboxOther: value, checkboxOtherChecked: !!value });
  };

  const compareToFirstPassword = (_: any, value: string) => {
    if (value && value !== form.getFieldValue("password")) {
      return Promise.reject("Passwords do not match!");
    }
    return Promise.resolve();
  };

  const validateToNextPassword = (_: any, value: string) => {
    if (value && state.confirmDirty) {
      form.validateFields(["confirm"]);
    }
    return Promise.resolve();
  };

  const onChange = (e: RadioChangeEvent) => {
    setState({ ...state, radioValue0: e.target.value });
    form.setFieldsValue({ olsExperience: e.target.value });
  };

  const handleFinishFailed = (errorInfo: any) => {
    console.log("Form validation failed:", errorInfo);
    const errorFields = errorInfo.errorFields.map((field: any) => `${field.name}: ${field.errors.join(", ")}`);
    console.log("Specific validation errors:", errorFields);
    message.error(`Please fill in all required fields correctly: ${errorFields.join("; ")}`);
  };

  if (!userStore.isLoaded) {
    return <Spinner />;
  }

  return (
    <RegisterContainer>
      <Helmet>
        <title>OLSME Subscription Registration | onelightsystem OLS - Sun Light Meditation</title>
        <meta
          name="description"
          content="Join onelightsystem OLS with an OLSME subscription for Sun Light Meditation education and Light Civilization access. Register now for individual or corporate packages!"
        />
        <meta
          name="keywords"
          content="OLSME subscription registration, Sun Light Meditation, OLS subscription, Light Civilization, onelightsystem OLS, meditation courses, spiritual growth"
        />
        <meta name="author" content="onelightsystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="OLSME Subscription Registration | onelightsystem OLS - Sun Light Meditation" />
        <meta
          property="og:description"
          content="Join onelightsystem OLS with an OLSME subscription for Sun Light Meditation education and Light Civilization access. Register now for individual or corporate packages!"
        />
        <meta property="og:url" content="https://www.olsme.com/register/olsmeregister" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="onelightsystem OLS Logo for OLSME Subscription Registration Page" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="onelightsystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:title" content="OLSME Subscription Registration | onelightsystem OLS - Sun Light Meditation" />
        <meta
          name="twitter:description"
          content="Join onelightsystem OLS with an OLSME subscription for Sun Light Meditation education and Light Civilization access. Register now for individual or corporate packages!"
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="onelightsystem OLS Logo for OLSME Subscription Registration Page" />
        <link rel="canonical" href="https://www.olsme.com/register/olsmeregister" />
        <link rel="preload" href="/logo.jpg" as="image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "OLSME Subscription Registration",
            "url": "https://www.olsme.com/register/olsmeregister",
            "description": "Join onelightsystem OLS with an OLSME subscription for Sun Light Meditation education and Light Civilization access. Register now for individual or corporate packages.",
            "mainEntity": {
              "@type": "Service",
              "name": "OLSME Subscription Registration",
              "description": "Subscription service for onelightsystem OLS, offering Sun Light Meditation education and Light Civilization community access for individuals, corporations, or governmental entities.",
              "provider": {
                "@type": "Organization",
                "name": "onelightsystem OLS",
                "url": "https://www.olsme.com",
                "logo": "https://www.olsme.com/logo.jpg",
                "contactPoint": {
                  "@type": "ContactPoint",
                  "contactType": "customer service",
                  "email": "info@olsme.com",
                  "telephone": "+1-312-945-6172",
                },
                "sameAs": [
                  "https://www.linkedin.com/company/onelightsystem",
                  "https://twitter.com/onelightsystem",
                  "https://www.facebook.com/onelightsystem",
                  "https://www.instagram.com/onelightsystem",
                  "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
                ],
              },
            },
            "publisher": {
              "@type": "Organization",
              "name": "onelightsystem OLS",
              "url": "https://www.olsme.com",
              "logo": "https://www.olsme.com/logo.jpg",
            },
            "offers": {
              "@type": "Offer",
              "name": "OLSME Subscription",
              "description": "Public and corporate packages for Sun Light Meditation training and energy alignment.",
              "url": "https://www.olsme.com/register/olsmeregister",
              "availability": "https://schema.org/InStock",
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://www.olsme.com",
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Register",
                  "item": "https://www.olsme.com/register",
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "OLSME Subscription Registration",
                  "item": "https://www.olsme.com/register/olsmeregister",
                },
              ],
            },
          })}
        </script>
      </Helmet>
      <BreadcrumbWrapper>
        <Breadcrumb>
          <Breadcrumb.Item>
            <RouterLink to="/">
              <HomeOutlined /> Home
            </RouterLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <RouterLink to="/register">Register</RouterLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item>OLSME Subscription Registration</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbWrapper>
      <BackButton />
      <RegisterCard>
        <StyledForm
          form={form}
          onFinish={handleSubmit}
          onFinishFailed={handleFinishFailed}
          layout="vertical"
          className="olsme-form"
        >
          <div style={{ color: "#0427f4", textAlign: "center", marginBottom: "20px" }}>
            <Title style={{ color: "#0427f4" }} level={1}>
              OLSME Subscription Service Evaluation Fees Application
            </Title>
            <Text style={{ color: "#0427f4", display: "block", marginBottom: "20px" }}>
              Subscribe to onelightsystem OLS for Sun Light Meditation education
            </Text>
          </div>

          <SectionDivider orientation="left">
            <UserOutlined style={{ marginRight: 8 }} /> Account Information
          </SectionDivider>
          <Form.Item
            label={
              <span>
                <MailOutlined /> E-mail
              </span>
            }
            name="email"
            rules={[
              { type: "email", message: "Invalid email address!" },
              { required: true, message: "Email is required!" },
            ]}
            tooltip={{
              title: "Enter your verified email address for OLSME subscription.",
              icon: <QuestionCircleOutlined />,
            }}
          >
            <Input disabled={userStore.isLoggedIn} />
          </Form.Item>
          {!userStore.isLoggedIn && (
            <>
              <Form.Item
                label={
                  <span>
                    <InfoCircleOutlined /> Password
                  </span>
                }
                name="password"
                rules={[
                  { required: true, message: "Password is required!" },
                  { min: 6, message: "Password must be at least 6 characters!" },
                  { validator: validateToNextPassword },
                ]}
                hasFeedback
              >
                <Input.Password />
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    <InfoCircleOutlined /> Confirm Password
                  </span>
                }
                name="confirm"
                rules={[
                  { required: true, message: "Confirm your password!" },
                  { validator: compareToFirstPassword },
                ]}
                hasFeedback
              >
                <Input.Password
                  onBlur={(e) => setState({ ...state, confirmDirty: !!e.target.value })}
                />
              </Form.Item>
            </>
          )}
          <Form.Item
            label={
              <span>
                <UserOutlined /> Name
              </span>
            }
            name="name"
            rules={[{ required: true, message: "Name is required!", whitespace: true }]}
            tooltip={{
              title: "Enter your full name (First and Last).",
              icon: <QuestionCircleOutlined />,
            }}
          >
            <Input />
          </Form.Item>

          <SectionDivider orientation="left">
            <InfoCircleOutlined style={{ marginRight: 8 }} /> Subscription Details
          </SectionDivider>
          <Form.Item
            label={
              <span>
                <InfoCircleOutlined /> Onelightsystem OLS Experience
              </span>
            }
            name="olsExperience"
            rules={[{ required: true, message: "OLS experience is required!", whitespace: true }]}
          >
            <Radio.Group onChange={onChange} value={state.radioValue0}>
              <Radio style={radioStyle} value="New User">New</Radio>
              <Radio style={radioStyle} value="PAST Initiated OLS student">PAST Initiated OLS student</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={
              <span>
                <GlobalOutlined /> OLSME Subscription Account Type
              </span>
            }
            name="accountType"
            rules={[{ required: true, message: "Account type is required!", whitespace: true }]}
            tooltip={{
              title: "Select your subscription usage to evaluate fees (Individual, Corporation, or Governmental Entity).",
              icon: <QuestionCircleOutlined />,
            }}
          >
            <Radio.Group>
              <Radio style={radioStyle} value="Individual">
                Individual
                <Tooltip title="(Subscription fees based on local GDP)">
                  <QuestionCircleOutlined />
                </Tooltip>
              </Radio>
              <Radio style={radioStyle} value="Corporation">
                Corporation
                <Tooltip title="(Fees based on size and volume)">
                  <QuestionCircleOutlined />
                </Tooltip>
              </Radio>
              <Radio style={radioStyle} value="Governmental Entity">
                Governmental Entity
                <Tooltip title="(Fees based on state and country)">
                  <QuestionCircleOutlined />
                </Tooltip>
              </Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={
              <span>
                <InfoCircleOutlined /> OLS Health Package
              </span>
            }
            name="credits"
          >
            <CreditsGrid>
              <Checkbox value="OLS Health">
                <Tooltip title="OLS health package">
                  <span style={{ color: "#0427f4" }}>
                    I am interested in getting the OLS health package
                  </span>
                </Tooltip>
              </Checkbox>
            </CreditsGrid>
          </Form.Item>
          <Form.Item
            label={
              <span>
                <InfoCircleOutlined /> How Did You Find Out About OLS
              </span>
            }
            name="reference"
            rules={[{ required: true, message: "Referral information is required!", whitespace: true }]}
            tooltip={{
              title: "Was it through an OLS promoter or referral?",
              icon: <QuestionCircleOutlined />,
            }}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={
              <span>
                <InfoCircleOutlined /> Referral Code (Optional)
              </span>
            }
            name="referralCode"
            tooltip={{
              title: "Enter a referral code to receive a 5% commission on your registration fees.",
              icon: <QuestionCircleOutlined />,
            }}
          >
            <Input placeholder="Enter referral code" />
          </Form.Item>
          <Form.Item
            label={
              <span>
                <GlobalOutlined /> Location (Country, City/Town)
              </span>
            }
            name="location"
            rules={[{ required: true, message: "Location is required!", whitespace: true }]}
            tooltip={{
              title: "Fees for 90 days: CA$9 (Bay Area CA $12, USA average $7); other countries based on Quality of Life GDP value.",
              icon: <QuestionCircleOutlined />,
            }}
          >
            <Input />
          </Form.Item>

          <SectionDivider orientation="left">Agreements</SectionDivider>
          <Form.Item
            label={
              <span>
                <InfoCircleOutlined /> Verification
              </span>
            }
            name="agreement1"
            valuePropName="checked"
            rules={[{ required: true, message: "Please agree to verify information!" }]}
          >
            <Checkbox>All the information submitted in this form is true and correct</Checkbox>
          </Form.Item>
          <Form.Item
            label={
              <span>
                <InfoCircleOutlined /> Agreement
              </span>
            }
            name="agreement2"
            valuePropName="checked"
            rules={[{ required: true, message: "Please agree to the terms of service!" }]}
          >
            <Checkbox>
              I have read and agreed to the{" "}
              <RouterLink to="/ols-private-privacy-disclosure-updates-122023/olsme" style={{ color: "#3552fc" }}>
                terms of service
              </RouterLink>
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={state.isSubmitting}
              aria-label={userStore.isLoggedIn ? "Update OLSME account" : "OLSME Signup"}
            >
              {userStore.isLoggedIn ? "Update OLSME account" : "OLSME Signup"}
            </Button>
          </Form.Item>
          <Text style={{ color: "#0427f4", marginTop: "20px", display: "block", textAlign: "center" }}>
            Need help?{" "}
            <RouterLink to="/contact" style={{ color: "#3552fc", textDecoration: "underline" }}>
              Contact us
            </RouterLink>{" "}
            or learn more about{" "}
            <RouterLink to="/olslm" style={{ color: "#3552fc", textDecoration: "underline" }}>
              OLSLM Assets
            </RouterLink>
          </Text>
        </StyledForm>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default withFirebase(OLSMERegister);